import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { apiHandler } from '../../../api/apihandler';
import {
  PROFILE_DETAILS,
  FEEDS_LIST,
  PEOPLE_YOU_MAY_KNOW,
  USER_FOLLOW_REQUEST,
  BLOCK_UNBLOCK_FOLLOWER,
} from '../../../api/constants';
import { getAvatarColor, getInitials, apiSucceeded, apiErrorMessage, formatAddress } from '../../../views/dashboard/feed/feedUtils';
import MediaPlaceholderIcon from '../../../components/nemonto/MediaPlaceholderIcon';
import './my-profile-page.css';

const TABS = [
  { key: 'recipes', label: 'Recipes' },
  { key: 'madeit', label: 'Made it' },
  { key: 'challenges', label: 'Challenges' },
];

const Avatar = ({ url, name, className }) => {
  if (url) return <img src={url} alt={name} className={className} />;
  return (
    <div className={className} style={{ background: getAvatarColor(name || 'User'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

// posted_challenges/participated_challenges/made_it_list all come back
// inline on PROFILE_DETAILS itself - no separate fetch needed, unlike
// recipes which has its own list endpoint. `profileOwner` is who to
// attribute a card to when the underlying post has no author of its own
// (e.g. a challenge this profile hosted) - the profile being viewed.
function mapPostedChallenge(c, profileOwner) {
  return {
    id: c.id,
    kind: 'challenge',
    title: c.title || c.content || 'Challenge',
    excerpt: c.content && c.content !== c.title ? c.content : null,
    image: c.user_post_images?.[0]?.image_path || null,
    badge: 'Hosted',
    ownerName: profileOwner.name,
    ownerPhoto: profileOwner.photo,
  };
}

function mapParticipatedChallenge(p, profileOwner) {
  const post = p?.user_post || {};
  const host = post?.user;
  const hostName = host ? `${host.first_name || ''} ${host.last_name || ''}`.trim() || 'User' : profileOwner.name;
  return {
    id: post.id,
    kind: 'challenge',
    title: post.title || post.content || 'Challenge',
    excerpt: post.content && post.content !== post.title ? post.content : null,
    image: post.user_post_images?.[0]?.image_path || null,
    badge: p?.is_winner === 1 ? 'Winner' : 'Joined',
    ownerName: hostName,
    ownerPhoto: host ? (host.user_setting?.photo || null) : profileOwner.photo,
  };
}

function mapMadeIt(m, profileOwner) {
  const recipeOwner = m.recipe?.user;
  const ownerName = recipeOwner ? `${recipeOwner.first_name || ''} ${recipeOwner.last_name || ''}`.trim() || 'User' : profileOwner.name;
  return {
    id: m.id,
    kind: 'recipe',
    linkId: m.original_recipe_id,
    title: m.recipe?.title || 'Made it',
    excerpt: m.content || null,
    image: m.media?.[0]?.file_url || m.recipe?.user_post_images?.[0]?.image_path || null,
    ownerName,
    ownerPhoto: recipeOwner ? (recipeOwner.user_setting?.photo || null) : profileOwner.photo,
  };
}

// Card design matches my-profile-page.js's RecipeCard (itself matching
// FavouriteRecipesPage's .nmsl-recipe-card) - thumb on top, owner row +
// title + excerpt below, 3-column grid via the shared my-profile-page.css.
const RecipeCard = ({ item, onOpen }) => (
  <div className="nmpr-recipe-card" onClick={() => onOpen(item)} role="button" tabIndex={0}>
    <div className="nmpr-recipe-thumb">
      {item.image ? <img src={item.image} alt="" /> : <MediaPlaceholderIcon size={48} />}
      {item.badge && <span className="nmpr-recipe-badge">{item.badge}</span>}
    </div>
    <div className="nmpr-recipe-body">
      <div className="nmpr-recipe-owner-row">
        <Avatar url={item.ownerPhoto} name={item.ownerName} className="nmpr-recipe-owner-avatar" />
        <span className="nmpr-recipe-owner-name">{item.ownerName}</span>
      </div>
      <p className="nmpr-recipe-title">{item.title || 'Untitled recipe'}</p>
      {item.excerpt && <p className="nmpr-recipe-excerpt">{item.excerpt}</p>}
    </div>
  </div>
);

// Nemonto-themed "view another cook's profile" screen - the live
// counterpart to my-profile-page.js, minus the self-only affordances (cover
// upload, edit profile, Saved tab) and with Follow/Message/Block actions in
// their place. Rendered by the "normal" (non-teacher) branch of
// src/views/dashboard/app/friend-profile.js so every existing link to
// /dashboard/app/friend-profile/:slug keeps working unchanged.
const FriendProfilePage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(0);
  const [followRequestStatus, setFollowRequestStatus] = useState('none');
  const [isBlocked, setIsBlocked] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);

  const [activeTab, setActiveTab] = useState('recipes');
  const [recipes, setRecipes] = useState([]);
  const [recipesLoaded, setRecipesLoaded] = useState(false);

  // posted_challenges/participated_challenges/made_it_list (and their
  // _count fields) come back as siblings of `user` on PROFILE_DETAILS, not
  // nested inside it - kept separate from `profile` for that reason.
  const [postedChallenges, setPostedChallenges] = useState([]);
  const [participatedChallenges, setParticipatedChallenges] = useState([]);
  const [madeItList, setMadeItList] = useState([]);
  const [challengeCounts, setChallengeCounts] = useState({ posted: 0, participated: 0 });
  const [madeItCount, setMadeItCount] = useState(0);

  const [suggestions, setSuggestions] = useState([]);
  const [busyFollowIds, setBusyFollowIds] = useState(new Set());

  async function fetchProfile() {
    setLoading(true);
    try {
      const isNumeric = slug !== undefined && slug !== null && !Number.isNaN(Number(slug));
      const res = await apiHandler(PROFILE_DETAILS, 'POST', isNumeric ? { user_id: slug } : { slug });
      if (apiSucceeded(res)) {
        const data = res?.data?.data;
        const user = data?.user;
        setProfile(user);
        setIsFollowing(user?.is_following || 0);
        setFollowRequestStatus(user?.follow_request_status || 'none');
        setIsBlocked(user?.is_blocked || 0);
        setPostedChallenges(data?.posted_challenges || []);
        setParticipatedChallenges(data?.participated_challenges || []);
        setMadeItList(data?.made_it_list || []);
        setChallengeCounts({
          posted: data?.posted_challenges_count ?? (data?.posted_challenges || []).length,
          participated: data?.participated_challenges_count ?? (data?.participated_challenges || []).length,
        });
        setMadeItCount(data?.made_it_count ?? (data?.made_it_list || []).length);
      } else {
        Swal.fire('Unable to load profile', apiErrorMessage(res, 'Please try again.'), 'warning');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchRecipes(userId) {
    try {
      const res = await apiHandler(FEEDS_LIST, 'POST', { user_id: userId, page: 1, limit: 50 });
      if (apiSucceeded(res)) {
        const raw = res?.data?.data?.feeds?.data || [];
        const ownerName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'User';
        const ownerPhoto = profile?.user_setting?.photo || null;
        setRecipes(raw.map((d) => ({
          id: d.id,
          kind: 'recipe',
          title: d.title || '',
          excerpt: d.content || null,
          image: d.user_post_images?.[0]?.image_path || null,
          ownerName,
          ownerPhoto,
        })));
      }
    } catch (e) { console.error(e); }
    finally { setRecipesLoaded(true); }
  }

  async function fetchSuggestions() {
    try {
      const res = await apiHandler(PEOPLE_YOU_MAY_KNOW, 'POST', { page: 1, limit: 4 });
      if (apiSucceeded(res)) setSuggestions(res?.data?.data?.users?.data || []);
    } catch (_) { /* suggestions stay empty */ }
  }

  useEffect(() => {
    fetchProfile();
    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (profile?.id && !recipesLoaded) fetchRecipes(profile.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // Own profile viewed through a friend link (e.g. a stale tagged-user
  // link) - send back to the real "My Profile" screen instead of showing a
  // read-only mirror of yourself with a Follow button.
  useEffect(() => {
    if (profile?.is_own === 1) navigate('/dashboard/app/profile', { replace: true });
  }, [profile, navigate]);

  async function toggleFollow() {
    if (!profile?.id || followBusy) return;
    setFollowBusy(true);
    try {
      const res = await apiHandler(USER_FOLLOW_REQUEST, 'POST', { follower_id: profile.id });
      if (apiSucceeded(res)) {
        if (isFollowing || followRequestStatus === 'accepted') {
          setIsFollowing(0);
          setFollowRequestStatus('none');
        } else if (followRequestStatus === 'pending') {
          setFollowRequestStatus('none');
        } else {
          setFollowRequestStatus('pending');
        }
      } else {
        Swal.fire('Error', apiErrorMessage(res, 'Unable to update follow status.'), 'error');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setFollowBusy(false);
    }
  }

  async function toggleBlock() {
    if (!profile?.id || blockBusy) return;
    setBlockBusy(true);
    try {
      const res = await apiHandler(BLOCK_UNBLOCK_FOLLOWER, 'POST', { follower_id: profile.id });
      if (apiSucceeded(res)) {
        setIsBlocked((prev) => (prev ? 0 : 1));
      } else {
        Swal.fire('Error', apiErrorMessage(res, 'Unable to update block status.'), 'error');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBlockBusy(false);
    }
  }

  async function toggleFollowSuggestion(userId) {
    if (busyFollowIds.has(userId)) return;
    setBusyFollowIds((prev) => new Set([...prev, userId]));
    try {
      const res = await apiHandler(USER_FOLLOW_REQUEST, 'POST', { follower_id: userId });
      if (apiSucceeded(res)) {
        setSuggestions((prev) => prev.map((u) => (u.id === userId ? { ...u, is_following: u.is_following === 1 ? 0 : 1 } : u)));
      }
    } catch (e) { console.error(e); }
    finally {
      setBusyFollowIds((prev) => { const next = new Set(prev); next.delete(userId); return next; });
    }
  }

  function openItem(item) {
    if (item.kind === 'challenge') {
      navigate(`/pages/challenge/${item.id}`);
    } else {
      navigate(`/pages/recipe/${item.linkId ?? item.id}`);
    }
  }

  function openMessage() {
    navigate(`/chat/user/${profile.id}`, {
      state: { header: { title: fullName, avatarUrl: profile?.user_setting?.photo, isGroup: false } },
    });
  }

  if (loading && !profile) {
    return <div className="nmpr-loading">Loading profile…</div>;
  }

  const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'User';
  const handle = profile?.slug ? `@${profile.slug}` : '';
  const bio = profile?.user_setting?.bio || '';
  const address = formatAddress(profile?.user_setting?.address);
  const profileOwner = { name: fullName, photo: profile?.user_setting?.photo || null };
  const challengeEntries = [
    ...postedChallenges.map((c) => mapPostedChallenge(c, profileOwner)),
    ...participatedChallenges.filter((p) => p?.user_post?.id).map((p) => mapParticipatedChallenge(p, profileOwner)),
  ];
  const madeItItems = madeItList.map((m) => mapMadeIt(m, profileOwner));

  const activeList = activeTab === 'challenges' ? challengeEntries
    : activeTab === 'madeit' ? madeItItems
      : recipes;

  const followLabel = isFollowing || followRequestStatus === 'accepted'
    ? 'Following'
    : followRequestStatus === 'pending'
      ? 'Requested'
      : 'Follow';
  const isFollowingState = followLabel !== 'Follow';

  return (
    <div className="nmpr-root">
      <div className="nmpr-app">
        <main className="nmpr-profile-col">
          <div className="nmpr-banner" style={profile?.user_setting?.cover_photo ? { backgroundImage: `url(${profile.user_setting.cover_photo})` } : undefined} />

          <div className="nmpr-identity-block">
            <div className="nmpr-id-top-row">
              <Avatar url={profile?.user_setting?.photo} name={fullName} className="nmpr-avatar-xl" />
              <div className="nmpr-friend-actions">
                <button
                  type="button"
                  className={`nmpr-follow-btn${isFollowingState ? ' following' : ''}`}
                  disabled={followBusy}
                  onClick={toggleFollow}
                >
                  {followLabel}
                </button>
                <button type="button" className="nmpr-message-btn" onClick={openMessage}>
                  💬 Message
                </button>
                <button
                  type="button"
                  className={`nmpr-block-btn${isBlocked ? ' blocked' : ''}`}
                  disabled={blockBusy}
                  onClick={toggleBlock}
                  title={isBlocked ? 'Unblock' : 'Block'}
                >
                  ⛔
                </button>
              </div>
            </div>

            <div className="nmpr-id-name-row">
              <span className="nmpr-id-name">{fullName}</span>
            </div>
            {handle && <p className="nmpr-id-handle">{handle}</p>}
            {bio && <p className="nmpr-id-bio">{bio}</p>}

            {address && (
              <div className="nmpr-id-meta-row">
                <span className="nmpr-id-meta">📍 {address}</span>
              </div>
            )}

            <div className="nmpr-stats-row">
              <div className="nmpr-stat-cell nmpr-clickable" role="button" tabIndex={0} onClick={() => setActiveTab('recipes')}>
                <div className="nmpr-stat-num">{profile?.post_count ?? 0}</div><div className="nmpr-stat-label">Recipes</div>
              </div>
              <div className="nmpr-stat-cell"><div className="nmpr-stat-num">{profile?.follower_count ?? 0}</div><div className="nmpr-stat-label">Followers</div></div>
              <div className="nmpr-stat-cell"><div className="nmpr-stat-num">{profile?.following_count ?? 0}</div><div className="nmpr-stat-label">Following</div></div>
              <div className="nmpr-stat-cell nmpr-clickable" role="button" tabIndex={0} onClick={() => setActiveTab('challenges')}>
                <div className="nmpr-stat-num nmpr-accent">{challengeCounts.posted + challengeCounts.participated}</div><div className="nmpr-stat-label">Challenges</div>
              </div>
            </div>
          </div>

          <div className="nmpr-profile-tabs">
            {TABS.map((tab) => (
              <div
                key={tab.key}
                className={`nmpr-ptab${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                role="button"
                tabIndex={0}
              >
                {tab.label}
                {tab.key === 'recipes' && <span className="nmpr-ptab-count">{recipes.length}</span>}
                {tab.key === 'madeit' && <span className="nmpr-ptab-count">{madeItCount}</span>}
                {tab.key === 'challenges' && <span className="nmpr-ptab-count">{challengeCounts.posted + challengeCounts.participated}</span>}
              </div>
            ))}
          </div>

          <div className="nmpr-grid-content">
            {activeList.length === 0 ? (
              <p className="nmpr-empty-note">
                {activeTab === 'recipes' && (recipesLoaded ? `${fullName} hasn't posted any recipes yet.` : 'Loading…')}
                {activeTab === 'madeit' && `${fullName} hasn't marked any recipes as made yet.`}
                {activeTab === 'challenges' && `${fullName} hasn't hosted or joined any challenges yet.`}
              </p>
            ) : (
              <div className="nmpr-recipe-grid">
                {activeList.map((item) => (
                  <RecipeCard key={`${item.kind}-${item.id}`} item={item} onOpen={openItem} />
                ))}
              </div>
            )}
          </div>
        </main>

        <aside className="nmpr-right-rail">
          <div className="nmpr-rail-block">
            <div className="nmpr-rail-head"><h3>People you may know</h3></div>
            {suggestions.length === 0 ? (
              <p className="nmpr-empty-note">No suggestions right now.</p>
            ) : suggestions.map((u) => {
              const name = `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || 'User';
              const isFollowingSuggestion = u?.is_following === 1;
              return (
                <div key={u.id} className="nmpr-suggestion-card">
                  <Avatar url={u?.user_setting?.photo} name={name} className="nmpr-sc-avatar" />
                  <div className="nmpr-sc-text">
                    <div className="nmpr-sc-name">{name}</div>
                    <div className="nmpr-sc-tag">{u?.follower_count ?? 0} followers</div>
                  </div>
                  <button
                    type="button"
                    className={`nmpr-sc-follow${isFollowingSuggestion ? ' following' : ''}`}
                    disabled={busyFollowIds.has(u.id)}
                    onClick={() => toggleFollowSuggestion(u.id)}
                  >
                    {isFollowingSuggestion ? 'Following' : 'Follow'}
                  </button>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default FriendProfilePage;
